import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stallsApi } from '@/api/stalls';
import { reportsApi, type VoteResponse } from '@/api/reports';
import { ApiHttpError, ApiNetworkError } from '@/api/client';
import { LivePill } from '@/components/LivePill';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/auth/AuthContext';
import { useToast } from '@/hooks/useToast';
import { GeoError, getCurrentLocation, withinGeofence } from '@/hooks/useGeofence';

interface VoteVars {
  status: 'open' | 'closed';
  origin: HTMLElement | null;
}

export function StallRoute() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const isTh = i18n.language.startsWith('th');
  const auth = useAuth();
  const toast = useToast();
  const qc = useQueryClient();

  const {
    data: stall,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['stall', id],
    queryFn: ({ signal }) => stallsApi.get(id!, signal),
    enabled: Boolean(id),
    retry: (count, e) => {
      if (e instanceof ApiHttpError && e.status < 500) return false;
      return count < 2;
    },
  });

  const voteMutation = useMutation<VoteResponse, unknown, VoteVars>({
    mutationFn: async ({ status }) => {
      if (!stall) throw new Error('stall_unavailable');
      if (!auth.user) throw new Error('sign_in_required');
      const token = await auth.getToken();
      if (!token) throw new Error('sign_in_required');
      const loc = await getCurrentLocation();
      if (!withinGeofence(loc, { lat: stall.lat, lng: stall.lng })) {
        // Match the server's error code so the toast picker maps to the
        // correct bilingual copy ("out_of_geofence" — see
        // opensoi-backend/internal/reports/handlers.go).
        throw new ApiHttpError(422, {
          code: 'out_of_geofence',
          message_en: "You're too far from the stall.",
          message_th: 'คุณอยู่ห่างจากแผงขายเกินไป',
        });
      }
      return reportsApi.vote(stall.id, status, { lat: loc.lat, lng: loc.lng }, token);
    },
    onSuccess: (rep, vars) => {
      const points = rep.points_awarded ?? 5;
      toast.burst(vars.origin, points);
      toast.show(t('vote.success_long'), points);
      void qc.invalidateQueries({ queryKey: ['stall', id] });
    },
    onError: (e) => {
      const msg =
        e instanceof GeoError
          ? e.kind === 'denied'
            ? t('vote.geo_denied')
            : e.kind === 'timeout'
              ? t('vote.geo_timeout')
              : t('vote.geo_unavailable')
          : e instanceof ApiHttpError
            ? e.code === 'rate_limited' || e.code === 'cooldown'
              ? t('vote.rate_limited')
              : e.code === 'out_of_geofence'
                ? t('vote.geofence')
                : e.message
            : e instanceof ApiNetworkError
              ? t('vote.network_error')
              : e instanceof Error && e.message === 'sign_in_required'
                ? t('vote.sign_in_required')
                : t('vote.network_error');
      toast.show(msg);
    },
  });

  const onVoteClick = (
    status: 'open' | 'closed',
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    voteMutation.mutate({ status, origin: e.currentTarget });
  };

  const title = useMemo(() => {
    if (!stall) return '';
    return isTh ? `${stall.name_th} · ${stall.name_en}` : `${stall.name_en} · ${stall.name_th}`;
  }, [stall, isTh]);

  if (!id) return <p className="p-4">{t('errors.not_found')}</p>;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[720px] px-4 py-6">
        <Skeleton height={280} rounded="md" />
        <div className="mt-6 space-y-3">
          <Skeleton width="60%" height={32} />
          <Skeleton width="40%" height={16} />
          <Skeleton width="100%" height={120} rounded="lg" />
        </div>
      </div>
    );
  }

  if (isError || !stall) {
    // 404 means the stall ID is wrong (or was deleted). Anything else
    // (5xx, network) is a transient problem — show retry, not "not found".
    const is404 = error instanceof ApiHttpError && error.status === 404;
    return (
      <div className="mx-auto max-w-[480px] px-4 py-12 text-center">
        <div className="mb-3 text-5xl" aria-hidden="true">
          {is404 ? '🥢' : '🛠️'}
        </div>
        <h1 className="m-0 mb-2 text-2xl font-bold">
          {is404 ? t('errors.not_found') : t('errors.stall_temporarily_unavailable')}
        </h1>
        {!is404 && error instanceof Error && (
          <p className="m-0 mb-4 text-sm text-ink-soft">{error.message}</p>
        )}
        <div className="flex flex-col items-center gap-2">
          {!is404 && (
            <button
              type="button"
              onClick={() => void qc.invalidateQueries({ queryKey: ['stall', id] })}
              className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold text-ink hover:bg-soi-100"
            >
              {t('errors.retry')}
            </button>
          )}
          <Link
            to="/"
            className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-soi-600 px-4 py-2 text-sm font-semibold text-white"
          >
            {t('errors.back_to_map')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-[720px] px-4 pb-16">
      <div
        className="mt-0 grid h-[280px] place-items-center text-7xl"
        style={{
          background: 'linear-gradient(135deg, var(--soi-100), var(--soi-400))',
          borderBottom: '1px solid var(--border)',
        }}
        aria-hidden="true"
      >
        🍳
      </div>

      <header className="pt-6 pb-3">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="rounded-pill bg-soi-100 px-3 py-1 text-xs font-semibold text-soi-700">
            {isTh ? stall.neighborhood_th : stall.neighborhood_en}
          </span>
          <span className="rounded-pill bg-cream px-3 py-1 text-xs font-semibold text-ink-soft">
            {stall.category}
          </span>
        </div>
        <h1 className="m-0 text-3xl font-bold leading-tight">{title}</h1>
        <div className="mt-2 flex items-center gap-3 text-sm text-ink-soft">
          <span>
            ★ {stall.rating.toFixed(1)} · {stall.reviews} reviews
          </span>
          {stall.hours && <span className="tabular">{stall.hours}</span>}
        </div>
      </header>

      <section
        className="relative mt-4 overflow-hidden rounded-lg border border-[color:var(--border)] p-4"
        style={{
          background:
            'linear-gradient(180deg, var(--surface), color-mix(in srgb, var(--soi-50) 60%, var(--surface)))',
        }}
      >
        <div
          className="absolute left-0 top-0 h-full w-1"
          style={{ background: stall.status === 'open' ? 'var(--open)' : 'var(--closed)' }}
          aria-hidden="true"
        />
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <LivePill status={stall.status} justOpened={stall.just_opened} />
            {typeof stall.last_report_mins_ago === 'number' && (
              <span className="text-xs text-ink-muted tabular">
                · {t('live.last_report', { minutes: stall.last_report_mins_ago })}
              </span>
            )}
          </div>
        </div>
        <p className="mt-2 mb-3 text-sm text-ink-soft">
          {stall.open_votes_30m} open · {stall.closed_votes_30m} closed in last 30 min.
        </p>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={(e) => onVoteClick('open', e)}
            disabled={voteMutation.isPending}
            className="flex min-h-[56px] flex-1 items-center justify-center gap-2 rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-4 text-base font-bold transition hover:-translate-y-0.5 hover:border-[color:var(--open)] hover:text-[color:var(--open)] hover:shadow-s2 disabled:opacity-60"
            aria-busy={voteMutation.isPending}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            {t('vote.open')}
          </button>
          <button
            type="button"
            onClick={(e) => onVoteClick('closed', e)}
            disabled={voteMutation.isPending}
            className="flex min-h-[56px] flex-1 items-center justify-center gap-2 rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-4 text-base font-bold transition hover:-translate-y-0.5 hover:border-[color:var(--closed)] hover:text-[color:var(--closed)] hover:shadow-s2 disabled:opacity-60"
            aria-busy={voteMutation.isPending}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            {t('vote.closed')}
          </button>
        </div>

        {!auth.user && (
          <p className="mt-3 text-xs text-ink-muted">
            <Link to="/signin" className="underline">
              {t('vote.sign_in_required')}
            </Link>
          </p>
        )}

        <div
          className="mt-3 flex items-start gap-2 rounded-md border p-3 text-xs text-ink-soft"
          style={{
            background: 'color-mix(in srgb, var(--warn) 12%, var(--surface))',
            borderColor: 'color-mix(in srgb, var(--warn) 40%, transparent)',
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            Voting requires being within 100m. We re-check on the server with your GPS.
          </span>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="m-0 mb-3 text-xl font-bold">Info</h2>
        <dl className="grid grid-cols-[100px_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="font-semibold text-ink-muted">Hours</dt>
          <dd className="tabular">{stall.hours ?? '—'}</dd>
          <dt className="font-semibold text-ink-muted">Address</dt>
          <dd>{isTh ? stall.address_th : stall.address_en}</dd>
          <dt className="font-semibold text-ink-muted">Famous for</dt>
          <dd>{(isTh ? stall.dishes_th : stall.dishes_en).join(' · ')}</dd>
        </dl>
      </section>
    </article>
  );
}
